import React, { ReactNode } from "react";
import { useIsAuthenticated, useLogout, useGetIdentity } from "@refinedev/core";
import { AuthPage } from "./AuthPage";

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { data: isAuthenticated, isLoading: authLoading } =
    useIsAuthenticated();
  const {
    data: user,
    isLoading: isLoadingIdentity,
    error: identityError,
  } = useGetIdentity({
    queryOptions: {
      enabled: isAuthenticated?.authenticated === true,
    },
  });

  const { mutate: logout } = useLogout();

  // Show loading state while checking authentication
  if (
    authLoading ||
    (isAuthenticated?.authenticated === true && isLoadingIdentity)
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-10 rounded-xl shadow-lg text-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="m-0 text-gray-600">Loading...</p>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // If not authenticated, show auth page
  if (!isAuthenticated?.authenticated) {
    return <AuthPage />;
  }

  console.log("Auth data:", { isAuthenticated, user, identityError });

  // Get user display name safely
  const getUserDisplayName = () => {
    const userData = user as any;
    if (userData?.data?.name) return userData.data.name;
    if (userData?.data?.email) return userData.data.email;
    if (identityError) return "User (auth error)";
    return "User";
  };

  // If authenticated, render children with header
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header with user info and logout */}
      <header className="bg-white px-6 py-4 shadow-md border-b border-gray-200 flex justify-between items-center">
        <div>
          <h1 className="m-0 text-blue-500 text-2xl font-bold">
            Planer Admin Panel
          </h1>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-gray-600">Witaj, {getUserDisplayName()}</div>
          <button
            onClick={() => logout()}
            className="px-4 py-2 bg-red-500 text-white border-0 rounded-md cursor-pointer text-sm font-medium transition-colors hover:bg-red-600"
          >
            Wyloguj
          </button>
        </div>
      </header>

      {/* Main content */}
      <main>{children}</main>
    </div>
  );
};
